import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faFloppyDisk, faSpinner } from '@fortawesome/free-solid-svg-icons'
import DaisyDatePicker from '../DaisyDatePicker'
import { hrToolsService } from '../../services/hrToolsService'
import { invalidatePayPeriodCache } from '../../services/payPeriodService'
import { invalidateOperationConfigCache } from '../../hooks/useControlCenterOperationConfig'
import {
  DEFAULT_OPERATION_CONFIG,
  WEEKDAY_LABELS,
  payDateForPeriodEnd,
  payPeriodBounds,
  upcomingPayPeriods,
  type PayPeriodType,
  type PayrollConfig,
} from '../../utils/controlCenterOperationConfig'

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string
  hint?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={`flex flex-col gap-1.5 w-full ${className || ''}`}>
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint ? <span className="text-xs text-base-content/60">{hint}</span> : null}
    </div>
  )
}

function formatDay(d: Date) {
  return d.toLocaleDateString()
}

const HrPayrollConfigCard: React.FC = () => {
  const [draft, setDraft] = useState<PayrollConfig>({
    otMethod: DEFAULT_OPERATION_CONFIG.otMethod,
    weeklyThresholdHours: DEFAULT_OPERATION_CONFIG.weeklyThresholdHours,
    dailyThresholdHours: DEFAULT_OPERATION_CONFIG.dailyThresholdHours,
    weekStartsOn: DEFAULT_OPERATION_CONFIG.weekStartsOn,
    payPeriodType: DEFAULT_OPERATION_CONFIG.payPeriodType,
    biweeklyAnchorDate: DEFAULT_OPERATION_CONFIG.biweeklyAnchorDate,
    payDateWeekday: DEFAULT_OPERATION_CONFIG.payDateWeekday,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const next = await hrToolsService.getPayrollConfig()
        if (!cancelled) setDraft(next)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load payroll config')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const patch = (partial: Partial<PayrollConfig>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
    setMessage(null)
  }

  const preview = useMemo(() => {
    const full = { ...DEFAULT_OPERATION_CONFIG, ...draft }
    const current = payPeriodBounds(full)
    const [upcoming] = upcomingPayPeriods(full, new Date(), 1)
    const weekday = draft.payDateWeekday
    return {
      currentStart: current.start,
      currentEnd: current.end,
      currentPayDate: payDateForPeriodEnd(current.end, weekday),
      nextPayDate: upcoming?.payDate || null,
    }
  }, [draft])

  const onSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const next = await hrToolsService.savePayrollConfig(draft)
      setDraft(next)
      invalidatePayPeriodCache()
      invalidateOperationConfigCache()
      setMessage('Saved.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const weekdayName = WEEKDAY_LABELS[draft.payDateWeekday] || 'Friday'

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faClock} />
          Time & overtime
        </h2>
        <p className="text-sm text-base-content/70">
          Pay dates used by Control Center Time and by Profile → Advances.
          Payday is the first {weekdayName.toLowerCase()} after each pay period ends.
        </p>

        {error ? <div className="alert alert-error mt-2"><span>{error}</span></div> : null}
        {message ? <div className="alert alert-success mt-2"><span>{message}</span></div> : null}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-base-content/70">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading payroll config…
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            <div className="rounded-box border border-base-300 bg-base-200/60 p-3.5">
              <div className="text-sm font-medium">Overtime</div>
              <p className="mt-1 text-sm text-base-content/70">
                Hours over 40 in a week are overtime. This rule is fixed and cannot be changed.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Week starts on"
                hint="Workweek used for the 40-hour overtime rule."
                className="sm:col-span-2"
              >
                <select
                  className="select select-bordered w-full"
                  value={draft.weekStartsOn}
                  onChange={(e) => patch({ weekStartsOn: Number(e.target.value) })}
                >
                  {WEEKDAY_LABELS.map((label, value) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Pay period type" className="sm:col-span-2">
                <select
                  className="select select-bordered w-full"
                  value={draft.payPeriodType}
                  onChange={(e) => patch({ payPeriodType: e.target.value as PayPeriodType })}
                >
                  <option value="weekly">Weekly (same as week start)</option>
                  <option value="biweekly">Biweekly (14 days)</option>
                </select>
              </Field>
              <Field
                label="Biweekly anchor date"
                hint="First day of a known pay period. Required for accurate biweekly ranges."
                className="sm:col-span-2"
              >
                <DaisyDatePicker
                  className="w-full"
                  disabled={draft.payPeriodType !== 'biweekly'}
                  value={draft.biweeklyAnchorDate || ''}
                  onChange={(value) => patch({ biweeklyAnchorDate: value.trim() || null })}
                />
              </Field>
              <Field
                label="Pay date"
                hint={`Payday is the following ${weekdayName.toLowerCase()} after the pay period ends. Scheduled deductions use this date.`}
                className="sm:col-span-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-base-content/70">Following</span>
                  <select
                    className="select select-bordered"
                    value={draft.payDateWeekday}
                    onChange={(e) => patch({ payDateWeekday: Number(e.target.value) })}
                  >
                    {WEEKDAY_LABELS.map((label, value) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>

            <div className="rounded-box border border-base-300 bg-base-200/60 p-3 text-sm">
              <div>Current period: {formatDay(preview.currentStart)} – {formatDay(preview.currentEnd)}</div>
              <div>Payday for this period: {formatDay(preview.currentPayDate)}</div>
              <div>
                Next payday (used for upcoming deductions):{' '}
                {preview.nextPayDate ? formatDay(preview.nextPayDate) : '—'}
              </div>
            </div>

            <div>
              <button
                type="button"
                className="btn btn-primary gap-2"
                disabled={saving}
                onClick={() => void onSave()}
              >
                <FontAwesomeIcon
                  icon={saving ? faSpinner : faFloppyDisk}
                  className={saving ? 'animate-spin' : ''}
                />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HrPayrollConfigCard
